class MiniCart extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector('sticky-header');
    this.drawer = document.querySelector('cart-drawer');
    this.setAttribute('tabindex', '-1');
    this.setAttribute('aria-modal', 'true');
    this.onCartKeydown = this.onCartKeydown || this.handleCartKeydown.bind(this);
    this.onCartFocusin = this.onCartFocusin || ((event) => { this.lastFocusedElement = event.target; });
    this.addEventListener('keydown', this.onCartKeydown);
    this.addEventListener('focusin', this.onCartFocusin);
    this.detailsObserver = new MutationObserver(() => this.syncDrawerFocus());
    this.detailsObserver.observe(this.drawer.querySelector('details'), {
      attributes: true, attributeFilter: ['open', 'class']
    });
    // Content replacement can remove focus. Attribute/style changes must never reset it.
    this.contentObserver = new MutationObserver(() => {
      if (!this.focusIsOpen) return;
      if (!this.focusInitialized || (this.lastFocusedElement && !this.lastFocusedElement.isConnected && document.activeElement === document.body)) {
        this.focusInitialControl();
      }
    });
    this.contentObserver.observe(this, { childList: true, subtree: true });
    this.intersectionObserver = new IntersectionObserver(this.handleIntersection.bind(this));
    this.intersectionObserver.observe(this);
    this.syncDrawerFocus();
  }

  disconnectedCallback() {
    this.detailsObserver.disconnect();
    this.contentObserver.disconnect();
    this.intersectionObserver.disconnect();
    this.removeEventListener('keydown', this.onCartKeydown);
    this.removeEventListener('focusin', this.onCartFocusin);
    this.focusIsOpen = false;
  }

  syncDrawerFocus() {
    const details = this.drawer.querySelector('details');
    const isOpen = details.open && details.classList.contains('menu-opening');
    if (isOpen === this.focusIsOpen) return;
    this.focusIsOpen = isOpen;
    this.focusInitialized = false;
    if (isOpen) {
      removeTrapFocus();
      this.focusInitialControl();
    }
  }

  focusInitialControl() {
    const closeButton = this.querySelector('.cart-close-btn');
    this.focusInitialized = Boolean(closeButton);
    (closeButton || this).focus();
  }

  handleCartKeydown(event) {
    if (event.key !== 'Tab' || !this.focusIsOpen || event.defaultPrevented) return;
    // Recompute boundaries so AJAX updates and newly loaded recommendations are included.
    const elements = getFocusableElements(this).filter((element) =>
      element.tabIndex >= 0 && !element.matches(':disabled') &&
      !element.closest('[hidden], [inert]') && element.getClientRects().length &&
      getComputedStyle(element).visibility === 'visible'
    );
    const first = elements[0];
    const last = elements[elements.length - 1];
    if (!first) {
      event.preventDefault();
      this.focus();
    } else if (event.shiftKey && (event.target === first || event.target === this)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (event.target === last || event.target === this)) {
      event.preventDefault();
      first.focus();
    }
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);

    fetch(this.dataset.url)
      .then(response => response.text())
      .then(html => {
        document.getElementById('mini-cart').innerHTML =
          this.getSectionInnerHTML(html, '.shopify-section');
          
          document.dispatchEvent(new CustomEvent('cartdrawer:opened'));
      })
      .catch(e => {
        console.error(e);
      });
  }

  open() {
    const detailsElement = this.drawer.querySelector('details');
    if (detailsElement.hasAttribute('open')) {
      return;
    }
    
    this.drawer.openMenuDrawer();
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      if (document.getElementById(section.id)) {
        document.getElementById(section.id).innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    }));

    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'mini-cart',
        section: 'mini-cart',
        selector: '.shopify-section'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'mobile-cart-icon-bubble',
        section: 'mobile-cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }
}

customElements.define('mini-cart', MiniCart);
